import { VideoProject } from "@/services/bedrock";

import { isQaReviewStatus } from "./components/VideoProjectQaModal/qaReview";
import { extraStatusMenuActions } from "./statusMenuActions";
import { editorSubmitEvent } from "./submitEvent";

export type EditorPageActionIntent =
  | { type: "status_event"; event: VideoProject.Event; userId?: number }
  | { type: "submit"; event: VideoProject.Event.SUBMIT_FOR_QA_REVIEW | VideoProject.Event.COMPLETE }
  | { type: "save" }
  | { type: "qa_review" }
  | { type: "reopen" }
  | { type: "duplicate" }
  | { type: "archive" };

export interface EditorPageActionSpec {
  text: string;
  icon?: string;
  intent: EditorPageActionIntent;
}

export interface EditorPageActionMapping {
  primary?: EditorPageActionSpec;
  secondary?: EditorPageActionSpec;
  menu: EditorPageActionSpec[];
  coveredEvents: VideoProject.Event[];
}

const SUBMIT_EVENTS = new Set<VideoProject.Event>([
  VideoProject.Event.SUBMIT_FOR_QA_REVIEW,
  VideoProject.Event.COMPLETE,
]);

function submitMenuAction(action: { event: VideoProject.Event; label: string }): EditorPageActionSpec {
  return {
    text: action.label,
    intent: {
      type: "submit",
      event: action.event as VideoProject.Event.SUBMIT_FOR_QA_REVIEW | VideoProject.Event.COMPLETE,
    },
  };
}

export function getEditorPageActions(options: {
  status: VideoProject.Status;
  archived?: boolean | null;
  requiresQa?: boolean | null;
  canManageVideoProjectQueue?: boolean;
  hasPost?: boolean;
  currentUserId?: number;
}): EditorPageActionMapping {
  const { status, archived, requiresQa, canManageVideoProjectQueue, hasPost, currentUserId } = options;
  const coveredEvents: VideoProject.Event[] = [];
  let primary: EditorPageActionSpec | undefined;
  let secondary: EditorPageActionSpec | undefined;

  if (archived) {
    return {
      primary: { text: "Duplicate", icon: "copy", intent: { type: "duplicate" } },
      menu: [],
      coveredEvents,
    };
  }

  if (canManageVideoProjectQueue && isQaReviewStatus(status)) {
    primary = {
      text: status === VideoProject.Status.SUBMITTED_FOR_QA_REVIEW ? "Start QA review" : "Continue QA review",
      intent: { type: "qa_review" },
    };
    coveredEvents.push(VideoProject.Event.START_QA_REVIEW, VideoProject.Event.APPROVE_QA, VideoProject.Event.REJECT_QA);
  } else if (VideoProject.isCompletedStatus(status)) {
    secondary = { text: "Re-open", intent: { type: "reopen" } };
    coveredEvents.push(VideoProject.Event.REOPEN_EDITING);
  } else if (status === VideoProject.Status.SUBMITTED_FOR_EDITING) {
    primary = {
      text: "Start editing",
      intent: { type: "status_event", event: VideoProject.Event.START_EDITING, userId: currentUserId },
    };
    coveredEvents.push(VideoProject.Event.START_EDITING);
  } else if (status === VideoProject.Status.EDITING && hasPost) {
    const event = editorSubmitEvent(requiresQa);
    primary = {
      text: event === VideoProject.Event.SUBMIT_FOR_QA_REVIEW ? "Submit for QA Review" : "Send to client",
      intent: { type: "submit", event },
    };
    secondary = { text: "Save", intent: { type: "save" } };
    coveredEvents.push(event, VideoProject.Event.RESUBMIT);
    if (event === VideoProject.Event.SUBMIT_FOR_QA_REVIEW) {
      coveredEvents.push(VideoProject.Event.COMPLETE);
    }
  }

  const menu: EditorPageActionSpec[] = [];

  if (canManageVideoProjectQueue) {
    extraStatusMenuActions(status, { requiresQa, currentUserId, coveredEvents }).forEach(action => {
      if (SUBMIT_EVENTS.has(action.event)) {
        menu.push(submitMenuAction(action));
        return;
      }

      menu.push({
        text: action.label,
        intent: { type: "status_event", event: action.event, userId: action.userId },
      });
    });
  }

  menu.push({ text: "Duplicate", icon: "copy", intent: { type: "duplicate" } });
  menu.push({ text: "Archive", icon: "archive", intent: { type: "archive" } });

  return { primary, secondary, menu, coveredEvents };
}
